// Reset password page — Settings-02.
//
// Public route at ``/reset-password?token=…`` reached from the
// branded password-change email. POSTs to
// ``/api/v1/auth/password-reset/confirm``; the server consumes the
// token and revokes every active Session for the user, so a fresh
// sign-in is required after the redirect.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { AuthShell } from "@/features/auth";
import { useConfirmPasswordResetMutation } from "@/features/settings/hooks";


const MIN_PASSWORD_LENGTH = 12;


export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => (searchParams.get("token") || "").trim(),
    [searchParams],
  );
  const navigate = useNavigate();
  const mutation = useConfirmPasswordResetMutation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setValidationError("This reset link is missing a token.");
  }, [token]);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      );
      return;
    }
    if (password !== confirm) {
      setValidationError("Passwords don't match.");
      return;
    }
    if (!token) return;
    mutation.mutate(
      { token, new_password: password },
      {
        onSuccess: () => setSuccess(true),
      },
    );
  }

  return (
    <AuthShell layout="centered" testId="reset-password-shell">
      <div data-testid="reset-password-page" className="flex flex-col">
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-text-primary">
            Set a new password
          </h1>
          {!success && (
            <p className="text-sm text-text-secondary">
              Enter a new password for your BeneCRM account. You&rsquo;ll
              need to sign in again on every device.
            </p>
          )}
        </header>

        {success ? (
          <div className="mt-6 flex flex-col gap-4">
            <p
              role="status"
              data-testid="reset-password-success"
              className="rounded-md border border-sage/20 bg-sage-tint px-3 py-2 text-sm text-teal"
            >
              Password updated. Sign in with your new password.
            </p>
            <Link
              to="/login"
              data-testid="reset-password-go-to-login"
              className="inline-flex w-full items-center justify-center rounded-md bg-teal px-4 py-2.5 text-base font-medium text-text-inverse transition-base ease-out hover:opacity-90 focus-visible:shadow-focus focus-visible:outline-none"
            >
              Go to sign in
            </Link>
          </div>
        ) : (
          <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <label className="block">
              <span className="block text-sm font-medium text-text-primary">
                New password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                data-testid="reset-password-new"
                className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="mt-1 block text-xs text-text-tertiary">
                Use at least {MIN_PASSWORD_LENGTH} characters.
              </span>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-text-primary">
                Confirm new password
              </span>
              <input
                type="password"
                autoComplete="new-password"
                required
                data-testid="reset-password-confirm"
                className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>
            {validationError && (
              <div
                role="alert"
                data-testid="reset-password-validation-error"
                className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember"
              >
                {validationError}
              </div>
            )}
            {mutation.isError && !validationError && (
              <div
                role="alert"
                data-testid="reset-password-error"
                className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember"
              >
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : "This link is no longer valid. Request a fresh password change email."}
              </div>
            )}
            <button
              type="submit"
              disabled={mutation.isPending}
              data-testid="reset-password-submit"
              className="mt-2 w-full rounded-md bg-teal px-4 py-2.5 text-base font-medium text-text-inverse transition-base ease-out hover:opacity-90 focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-50"
            >
              {mutation.isPending ? "Saving…" : "Set new password"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="block w-full text-center text-xs font-medium text-text-tertiary transition-fast ease-out hover:text-text-primary"
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
